import { AppLauncher } from '@capacitor/app-launcher';
import { useT } from '@transifex/react';
import type { JSX, ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import * as endpoints from 'pkg/api/endpoints/auto';
import { useEndpoint } from 'pkg/api/use_endpoint';
import { resume } from 'pkg/events';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import useInterval from 'pkg/hooks/useInterval';
import { isApp, isIOS } from 'pkg/platform';

import { InfoScreen } from 'components/info_screen';

import MaintenanceMode from 'components/application/maintenance_mode';
import { Spinner } from 'components/loaders/spinner';

import Button from 'design/button';

export interface ApiConfig {
	socketUrl: string;
	adsEnabled: boolean;
	formsServiceUrl: string;
	maintenanceMode: boolean;
	// Whether the app requires an update to continue functioning
	updateRequired: boolean;
	// Is a new version of the app available?
	currentClientVersion: string;
	currentClientVersionTime: number;
}

interface ApiConfigContextProps extends ApiConfig {
	refreshConfig: () => void;
}

const apiConfig: ApiConfig = {
	socketUrl: '',
	adsEnabled: true,
	formsServiceUrl: '',
	maintenanceMode: false,
	updateRequired: false,
	currentClientVersion: '',
	currentClientVersionTime: 0,
};

const ApiConfigContext = createContext<ApiConfigContextProps>({
	...apiConfig,
	refreshConfig: () => null,
});

export function useApiConfig(): ApiConfigContextProps {
	const ctx = useContext(ApiConfigContext);
	return ctx;
}

interface ApiConfigProps {
	children: ReactNode;
}

export function ApiConfig({ children }: ApiConfigProps): JSX.Element {
	const t = useT();
	const [canOpenUrl, setCanOpenUrl] = useState(false);
	const url = isIOS()
		? 'https://apps.apple.com/se/app/360player/id1440982399'
		: 'market://details?id=com.threesixtyplayer.app';

	useComponentDidMount(async () => {
		if (isApp()) {
			const { value } = await AppLauncher.canOpenUrl({
				url,
			});

			setCanOpenUrl(value);
		}
	});

	const handleUpdate = async () =>
		await AppLauncher.openUrl({
			url,
		});

	const { record, isInitialLoad, refresh, hasError } = useEndpoint<ApiConfig>(
		endpoints.Configs.Index(),
		{}
	);

	useComponentDidMount(
		() => {
			resume.on(refresh);
		},
		() => {
			resume.off(refresh);
		}
	);

	// Check for new config every minute
	useInterval(() => {
		refresh();
	}, 1000 * 60);

	const val = useMemo(
		() => ({ ...record, refreshConfig: refresh }),
		[Object.values(record).toString(), refresh]
	);

	if (isInitialLoad) {
		return <Spinner center />;
	}

	if (record.updateRequired) {
		return (
			<InfoScreen
				icon="sync"
				heading={t(`Update required`)}
				message={t(`You must update your app to continue using 360Player.`)}>
				{canOpenUrl && (
					<Button label={t('Download update')} onClick={handleUpdate} />
				)}
			</InfoScreen>
		);
	}

	if (record.maintenanceMode) {
		return <MaintenanceMode refresh={refresh} />;
	}

	if (hasError) {
		return (
			<InfoScreen
				icon="error"
				heading={t(`Something went wrong`)}
				message={t(`The appplication failed to load, please try again later.`)}
			/>
		);
	}

	return (
		<ApiConfigContext.Provider value={val}>
			{children}
		</ApiConfigContext.Provider>
	);
}
